import { ApolloClient, ApolloLink, execute, InMemoryCache, ServerError } from '@apollo/client';
import omit from 'lodash/omit';
import type { Simplify } from 'type-fest';

import { trimStack, updateStack } from '@change-corgi/core/error';
import type { ReportableError, ReportOptions } from '@change-corgi/core/errorReporter/common';
import { isPerimeterXEnforcerChallenge } from '@change-corgi/core/perimeterx';

import { errorHandlerLink } from './errorHandlerLink';
import { wrapFetch } from './fetch';
import type { HttpLinkOptions, HttpLinks } from './getHttpLinks';
import { getHttpLinks } from './getHttpLinks';
import { makePromise } from './makePromise';
import { operationIdLink } from './operationIdLink';
import { parseQuery } from './parser';
import { responseInfoLink } from './responseInfoLink';
import type { GQLFetchRequest } from './types';
import { GQLResponseError, GQLResponsePerimeterXChallengeError, GQLResponseServerError } from './types';
import type { WebappInfo } from './webappInfoLink';
import { webappInfoLink } from './webappInfoLink';

export type Options = HttpLinkOptions & {
	reportError: (error: ReportableError, options?: ReportOptions) => void;
	reportNetworkError: (error: ReportableError) => void;
	addOperationNamesToUri?: boolean;
	webappInfo?: WebappInfo;
};

type DecoratedFetchResult<
	// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any
	TData = Record<string, any>,
	// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any
	TExtensions = Record<string, any>,
> = ApolloLink.Result<TData, TExtensions> & {
	/**
	 * The requestId coming from the response headers. Can be useful for debugging.
	 */
	requestId?: string;
};

type FetchResultWithoutErrors<DATA extends Record<string, unknown> = Record<string, unknown>> = Omit<
	DecoratedFetchResult<DATA>,
	'error' | 'data'
> & { data: DATA };
type FetchResultWithErrors<DATA extends Record<string, unknown> = Record<string, unknown>> = DecoratedFetchResult<DATA>;

// using this hack to avoid inferring types when no type parameters are passed
// https://www.typescriptlang.org/docs/handbook/utility-types.html#noinfertype does not work here
// eslint-disable-next-line @typescript-eslint/naming-convention
type BlockInfer = { __no_infer__: false };
type BlockInferWarning = { data: 'Please pass type parameters to GqlClient.fetch()' };
type BlockInferParamWarning = {
	query: 'Please pass type parameters to GqlClient.fetch()';
	variables: 'Please pass type parameters to GqlClient.fetch()';
};

export type GqlFetch = <
	DATA extends Record<string, unknown> = Record<string, unknown>,
	VARS extends Readonly<Record<string, unknown>> = Readonly<Record<string, unknown>>,
>(
	operation: GQLFetchRequest<VARS> & {
		rejectOnError: boolean;
	},
) => Promise<FetchResultWithoutErrors<DATA>>;

/* eslint-disable @typescript-eslint/consistent-type-definitions, @typescript-eslint/method-signature-style */
interface GqlClient {
	/**
	 * @throws {GQLResponseError} if `rejectOnError` is `true` and there are errors in the response body
	 * @throws {GQLResponseServerError} if the response is a server error
	 * @throws {GQLResponsePerimeterXChallengeError} if the response is a PerimeterX challenge
	 */
	fetch<DATA extends Record<string, unknown> = BlockInfer, VARS extends Readonly<Record<string, unknown>> = BlockInfer>(
		operation: VARS extends BlockInfer
			? Simplify<BlockInferParamWarning>
			: GQLFetchRequest<NoInfer<VARS>> & {
					/**
					 * if there are errors in the response body, rejects with a `GQLResponseError` instead of resolving
					 */
					rejectOnError: true;
				},
	): DATA extends BlockInfer
		? Promise<Simplify<BlockInferWarning>>
		: VARS extends BlockInfer
			? Promise<Simplify<BlockInferWarning>>
			: Promise<Simplify<FetchResultWithoutErrors<DATA>>>;
	/**
	 * @throws {GQLResponseError} if `rejectOnError` is `true` and there are errors in the response body
	 * @throws {GQLResponseServerError} if the response is a server error
	 * @throws {GQLResponsePerimeterXChallengeError} if the response is a PerimeterX challenge
	 */
	fetch<DATA extends Record<string, unknown> = BlockInfer, VARS extends Readonly<Record<string, unknown>> = BlockInfer>(
		operation: VARS extends BlockInfer
			? Simplify<BlockInferParamWarning>
			: GQLFetchRequest<NoInfer<VARS>> & {
					/**
					 * if there are errors in the response body, rejects with a `GQLResponseError` instead of resolving
					 */
					rejectOnError: false;
				},
	): DATA extends BlockInfer
		? Promise<Simplify<BlockInferWarning>>
		: VARS extends BlockInfer
			? Promise<Simplify<BlockInferWarning>>
			: Promise<Simplify<FetchResultWithErrors<DATA>>>;
}
/* eslint-enable @typescript-eslint/consistent-type-definitions, @typescript-eslint/method-signature-style */

class GqlClientImpl implements GqlClient {
	private readonly options: Options;
	private readonly links: HttpLinks;
	private readonly _fetch: typeof fetch;
	private readonly webappInfoLink: ApolloLink;
	private readonly errorHandlerLink: ApolloLink;
	private readonly client: ApolloClient;

	constructor(options: Options) {
		const { reportError, reportNetworkError, uri, headers, fetch: fetchOverride, addOperationNamesToUri } = options;
		this._fetch = wrapFetch({ fetch: fetchOverride || fetch, reportError, reportNetworkError, addOperationNamesToUri });
		this.options = options;
		this.links = getHttpLinks({ uri, headers, fetch: this._fetch });
		this.errorHandlerLink = errorHandlerLink(reportError);
		this.webappInfoLink = webappInfoLink(options.webappInfo);
		this.client = new ApolloClient({
			cache: new InMemoryCache(),
			defaultOptions: {
				watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'ignore' },
				query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
			},
			link: ApolloLink.empty(),
		});
	}

	private getUri(path: string | undefined) {
		const { uri } = this.options;
		return path ? `${uri}${path}` : uri;
	}

	async fetch<DATA extends Record<string, unknown> = BlockInfer>(
		operationP: unknown,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	): Promise<any> {
		// this is used to get the stacktrace of the caller instead of ending up
		// with an unhelpful stacktrace pointing to "fetch" in some browsers
		const { stack } = new Error();
		const stackTrim = 1;

		const operation = operationP as GQLFetchRequest & { rejectOnError: boolean };

		if (operation.path && !operation.path.startsWith('/')) {
			throw new Error('[gql.fetch] custom path must start with a slash');
		}

		const query = parseQuery(operation.query);
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
		const promise = makePromise(
			execute(
				/**
				 * operationIdLink => errorHandlerLink => responseInfoLink => httpLink
				 *                     (does nothing)      (does nothing)         |
				 *                                                                v
				 * operationIdLink <= errorHandlerLink <= responseInfoLink <= httpLink
				 *                  (needs response info) (adds response info
				 *                                           to extensions)
				 */
				ApolloLink.from([
					this.webappInfoLink,
					operationIdLink,
					this.errorHandlerLink,
					responseInfoLink,
					operation.batched === false ? this.links.single : this.links.batch,
				]),
				{
					...omit(operation, ['batched', 'path', 'rejectOnError']),
					context: {
						...operation.context,
						// this will override the uri set in getHttpLinks()
						uri: this.getUri(operation.path),
						stack,
						stackTrim,
					},
					query,
				},
				{ client: this.client },
			),
		) as Promise<FetchResultWithErrors<DATA>>;

		return handleGqlResponse<DATA>(promise, {
			rejectOnError: operation.rejectOnError,
			stack,
			stackTrim,
		});
	}
}

export async function handleGqlResponse<DATA extends Record<string, unknown> = Record<string, unknown>>(
	promise: Promise<DecoratedFetchResult<DATA>>,
	options: { rejectOnError: boolean; stack: string | undefined; stackTrim: number },
): Promise<FetchResultWithErrors<DATA> | FetchResultWithoutErrors<DATA>> {
	try {
		const result = await promise;
		const requestId = result.extensions?.requestId as string | undefined;
		if (options.rejectOnError) {
			if (result.errors) {
				// remove first line of stacktrace which would be the current function
				throw trimStack(
					updateStack(
						new GQLResponseError({
							errors: result.errors,
							requestId,
						}),
						options.stack,
					),
					options.stackTrim,
				);
			}
			if (!result.data) {
				// remove first line of stacktrace which would be the current function
				throw trimStack(
					updateStack(
						new GQLResponseError({
							message: 'No data in GQL response',
							errors: [],
							requestId,
						}),
						options.stack,
					),
					options.stackTrim,
				);
			}
		}
		return { ...result, requestId };
	} catch (err) {
		throw trimStack(updateStack(serverErrorToGqlResponseError(err), options.stack), options.stackTrim);
	}
}

function parseBodyText(bodyText: string) {
	try {
		if (typeof bodyText === 'string') {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return JSON.parse(bodyText);
		}
		return bodyText;
	} catch {
		return undefined;
	}
}

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */
function serverErrorToGqlResponseError(err: any) {
	if (err.name !== 'ServerError' && !(err instanceof Error) && err.status) {
		const response = err as Response;
		// this appears to be a Response object that was not transformed into a ServerError
		try {
			throw new ServerError(response.statusText, { response, bodyText: response.body as unknown as string });
		} catch (serverError) {
			// eslint-disable-next-line no-param-reassign
			err = serverError;
		}
	}
	if (err.name === 'ServerError') {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
		const serverError = err as ServerError;
		const bodyResult = parseBodyText(serverError.bodyText);
		if (serverError.statusCode === 403 && isPerimeterXEnforcerChallenge(bodyResult)) {
			return new GQLResponsePerimeterXChallengeError({ serverError, pxChallenge: bodyResult });
		}
		const errors = (() => {
			if (!Array.isArray(bodyResult.errors)) {
				return bodyResult.errors || [];
			}
			return [].concat(...bodyResult.filter((result: any) => !!result.errors).map((result: any) => result.errors));
		})();
		return new GQLResponseServerError({
			errors,
			serverError,
			requestId: serverError.response.headers.get('x-request-id') || undefined,
		});
	}
	return err;
}
/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */

export type { GqlClient };

export function createGqlClient(options: Options): GqlClient {
	return new GqlClientImpl(options);
}

export function createGqlClientFake(errorMsg: string): GqlClient {
	const errorFn = async () => {
		return Promise.reject(new Error(errorMsg));
	};
	return {
		fetch: errorFn,
	};
}
